"use client";

import { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, RefreshCw, LogOut, Check, PanelLeft, PanelBottom, Building2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NavItem as NavItemType } from "@/config/navConfig";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSidebarStore } from "@/store/useSidebarStore";
import { useUserStore } from "@/store/useUserStore";
import { useGroupedNav } from "@/hooks/useGroupedNav";
import { StreakCard } from "@/components/portal/common/learner/StreakCard";
import type { UserRole } from "@/types/userType";

// --- UI Components ---
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// --- Helper Hook: Detect Mobile Screen ---
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// --- Account Options Data ---
const accounts = [
  { type: 'super_admin', label: 'Super Admin', icon: 'S', colorBg: 'bg-orange-100', colorText: 'text-orange-600' },
  { type: 'admin', label: 'Admin', icon: 'A', colorBg: 'bg-red-100', colorText: 'text-red-600' },
  { type: 'team_manager', label: 'Manager', icon: 'M', colorBg: 'bg-blue-100', colorText: 'text-blue-600' },
  { type: 'learner', label: 'Learner', icon: 'L', colorBg: 'bg-purple-100', colorText: 'text-purple-600' },
];

// ... [IconRenderer Component remains unchanged] ...
export const IconRenderer = ({
  icon, 
  size = 20, 
  className = "",
  iconType = "icon",
  isActive = false
}: { 
  icon: NavItemType['icon']; 
  size?: number; 
  className?: string;
  iconType?: "icon" | "image" | "svg";
  isActive?: boolean;
}) => {
  if (iconType === 'icon') {
    const Icon = icon as LucideIcon;
    // Lucide icons default to strokeWidth 2, which looks heavier than the custom
    // SVG icons — thin them to 1.25 so the nav icons read at a consistent weight.
    return <Icon size={size} strokeWidth={1.25} className={className} />;
  }
  
  if (iconType === 'svg') {
    const coloredSvg = isActive 
      ? (icon as string).replace(/fill="[^"]*"/g, 'fill="currentColor"')
      : (icon as string);
    
    return (
      <div 
        className={cn("inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
        dangerouslySetInnerHTML={{ __html: coloredSvg }}
      />
    );
  }
  
  if (iconType === 'image') {
    return (
      <img 
        src={icon as string} 
        alt="" 
        className={cn("object-contain", className)}
        style={{ width: size, height: size }}
      />
    );
  }
  
  return null;
};

export const SidebarContent = ({ forceExpanded = false }: { forceExpanded?: boolean }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { close, isCollapsed: isCollapsedPref, toggleCollapse, mobileNavMode, setMobileNavMode } = useSidebarStore();
  // `isCollapsed` is a desktop-only preference. When rendered inside the mobile
  // drawer we force the full expanded view so labels always show — otherwise a
  // user who collapsed the desktop sidebar would see an icons-only drawer.
  const isCollapsed = forceExpanded ? false : isCollapsedPref;
  
  // ✅ FIX: Split selectors to prevent infinite loops / max depth error
  const role = useUserStore((state) => state.role);
  const setRole = useUserStore((state) => state.setRole);
  const user = useUserStore((state) => state.user);
  const logout = useUserStore((state) => state.logout);
  
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // 2. Compute Available Options based on User's roles
  const availableOptions = useMemo(() => {
    const rolesForOptions = (user?.roles && user.roles.length > 0) ? user.roles : (role ? [role as UserRole] : []);
    return accounts.filter((acc) => rolesForOptions.includes(acc.type));
  }, [user, role]);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const handleSwitchAccount = (accountType: string) => {
    if (accountType === role) return;
    console.log("Switching to:", accountType);
    setRole(accountType as UserRole);
    setOpen(false);
    navigate('/portal');
  };

  // Role + permission filtered nav, grouped into scope sections for super
  // admins / multi-workspace partners (flat for everyone else) — shared logic
  // with the mobile BottomNav.
  const { showSections, groups } = useGroupedNav();

  return (
    <div className={cn("h-full flex flex-col bg-white border-r border-zinc-200 transition-all duration-300", isCollapsed ? "w-[80px]" : "w-full")}>

      {/* Logo — crossfades between the full wordmark (expanded) and the icon mark (collapsed) */}
      <div className={cn("h-16 flex items-center flex-shrink-0", isCollapsed ? "justify-center px-2" : "px-4 justify-between")}>
        <div className={cn("relative h-10 flex items-center", isCollapsed ? "w-12" : "flex-1 min-w-0 mr-2")}>
          {/* Full wordmark */}
          <img
            src="/logo_b.svg"
            alt={`${import.meta.env.VITE_APP_NAME} logo`}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 h-8 w-auto max-w-full object-contain transition-all duration-300 ease-in-out",
              isCollapsed ? "opacity-0 scale-90 pointer-events-none" : "opacity-100 scale-100",
            )}
          />
          {/* Icon mark */}
          <img
            src="/brandico.svg"
            alt=""
            aria-hidden="true"
            className={cn(
              "absolute h-10 w-10 object-contain transition-all duration-300 ease-in-out",
              isCollapsed
                ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100 scale-100"
                : "left-0 top-1/2 -translate-y-1/2 opacity-0 scale-90 pointer-events-none",
            )}
          />
        </div>
        <div className="hidden lg:block">
          <button
            onClick={toggleCollapse}
            aria-label="Collapse sidebar"
            className={cn("cursor-pointer text-zinc-400 hover:text-zinc-600 p-1.5 rounded-lg transition-colors", isCollapsed && "hidden")}
          >
            <PanelLeft size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1 py-2 overflow-y-auto">
        <nav className={cn(isCollapsed ? "px-2" : "px-4")}>
          {groups.map((group) => (
            <div key={group.key} className={cn("flex flex-col gap-1", isCollapsed && "items-center")}>
              {showSections && group.key === "platform" && (
                <NavSectionHeader label={group.label ?? ""} isCollapsed={isCollapsed} />
              )}
              {showSections && group.key === "workspace" && (
                <WorkspaceNavHeader isCollapsed={isCollapsed} />
              )}
              {group.items.map((item) => (
                <NavItem
                  key={item.href}
                  item={item}
                  currentPath={location.pathname}
                  onLinkClick={close}
                  isCollapsed={isCollapsed}
                />
              ))}
            </div>
          ))}
        </nav>
      </ScrollArea>

      <div className={cn("p-3 space-y-2 shrink-0", isCollapsed && "flex flex-col items-center")}>

        {/* Learner activity streak. Renders null for non-learners and for
            learners who haven't started a streak, so it costs nothing here. */}
        <StreakCard isCollapsed={isCollapsed} />

        {/* Mobile navigation style — lets the user choose between the classic
            slide-in drawer and a bottom navigation bar. Mobile-only setting. */}
        {!isCollapsed && (
          <div className="lg:hidden">
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
              Mobile menu
            </p>
            <div className="flex items-center gap-1 rounded-xl bg-zinc-100 p-1">
              <button
                type="button"
                onClick={() => setMobileNavMode("drawer")}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                  mobileNavMode === "drawer" ? "bg-white text-brand-700 shadow-sm" : "text-zinc-500 hover:text-zinc-700",
                )}
              >
                <PanelLeft size={14} /> Drawer
              </button>
              <button
                type="button"
                onClick={() => setMobileNavMode("bottom")}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                  mobileNavMode === "bottom" ? "bg-white text-brand-700 shadow-sm" : "text-zinc-500 hover:text-zinc-700",
                )}
              >
                <PanelBottom size={14} /> Bottom bar
              </button>
            </div>
          </div>
        )}

        {availableOptions.length > 1 && (
          <>
            {isMobile ? (
              // --- MOBILE ---
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  {/* ✅ Define Button inline to ensure ref stability for Radix UI */}
                  <button
                    title={isCollapsed ? "Switch Role" : undefined}
                    className={cn("cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors", isCollapsed && "h-11 w-11 justify-center px-0")}
                  >
                    <RefreshCw size={20} className="shrink-0" />
                    {!isCollapsed && <span>Switch Role</span>}
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px] w-[90%] rounded-xl">
                  <DialogHeader>
                    <DialogTitle>Switch Account</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-2 py-2">
                    {availableOptions.map((acc) => {
                      const isCurrentRole = acc.type === role;
                      return (
                        <div 
                          key={acc.type}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-md transition-colors border",
                            isCurrentRole 
                              ? "bg-gray-50 border-gray-200 cursor-default opacity-100" 
                              : "border-transparent hover:bg-gray-50 cursor-pointer"
                          )}
                          onClick={() => !isCurrentRole && handleSwitchAccount(acc.type)}
                        >
                          <div className={cn("h-10 w-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0", acc.colorBg, acc.colorText)}>
                            {acc.icon}
                          </div>
                          <div className="flex-1 min-w-0 flex justify-between items-center">
                            <p className="font-medium text-sm text-gray-900">{acc.label}</p>
                            {isCurrentRole && <Check size={16} className="text-gray-500" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              // --- DESKTOP ---
              <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                  {/* ✅ Define Button inline */}
                  <button
                    title={isCollapsed ? "Switch Role" : undefined}
                    className={cn("cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors", isCollapsed && "h-11 w-11 justify-center px-0")}
                  >
                    <RefreshCw size={20} className="shrink-0" />
                    {!isCollapsed && <span>Switch Role</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="right" className="w-64 ml-2">
                  {availableOptions.map((acc) => {
                    const isCurrentRole = acc.type === role;
                    return (
                      <DropdownMenuItem 
                        key={acc.type}
                        className={cn(
                          "flex items-center gap-3 p-3 mb-1 last:mb-0 rounded-md border", // Added rounded-md and base border
                          isCurrentRole 
                             ? "bg-gray-50 border-gray-200 cursor-default opacity-100 data-[disabled]:opacity-100" // ✅ Border visible, Opacity fixed
                             : "border-transparent cursor-pointer focus:bg-gray-50" // Transparent border prevents layout shift
                        )}
                        disabled={isCurrentRole}
                        onClick={(e) => {
                          if (isCurrentRole) {
                            e.preventDefault();
                            return;
                          }
                          handleSwitchAccount(acc.type);
                        }}
                      >
                        <div className={cn("h-10 w-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0", acc.colorBg, acc.colorText)}>
                          {acc.icon}
                        </div>
                        <div className="flex-1 min-w-0 flex justify-between items-center">
                          <p className={cn("text-sm", isCurrentRole ? "font-semibold text-gray-900" : "font-medium text-gray-700")}>
                            {acc.label}
                          </p>
                          {isCurrentRole && <Check size={16} className="text-gray-500" />}
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}

        <button
          onClick={handleLogout}
          title={isCollapsed ? "Logout" : undefined}
          className={cn("cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors", isCollapsed && "h-11 w-11 justify-center px-0")}
        >
          <LogOut size={20} className="shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

// Scope-section header (e.g. "Platform"). Collapsed sidebar can't show text, so
// it degrades to a thin divider that still visually separates the groups.
const NavSectionHeader = ({ label, isCollapsed }: { label: string; isCollapsed: boolean }) => {
  if (isCollapsed) {
    return <div className="mx-2 my-2 w-11 border-t border-zinc-100" aria-hidden="true" />;
  }
  return (
    <p className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
      {label}
    </p>
  );
};

// "Current Workspace" section header — shows the selected workspace and, on
// click, opens the same top-bar workspace switcher (shared open-state) rather
// than duplicating it. Collapsed, it degrades to a small workspace-logo button.
const WorkspaceNavHeader = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const workspaces = useUserStore((s) => s.workspaces);
  const selectedWorkspaceId = useUserStore((s) => s.selectedWorkspaceId);
  const openSwitcher = useSidebarStore((s) => s.setWorkspaceSwitcherOpen);
  const selected = workspaces.find((w) => w.id === selectedWorkspaceId);
  const name = selected?.name ?? "Select workspace…";

  const renderLogo = (size: number) =>
    selected?.logo_url ? (
      <img
        src={selected.logo_url}
        alt=""
        className="shrink-0 rounded object-cover border border-zinc-100"
        style={{ width: size, height: size }}
      />
    ) : (
      <span
        className="flex shrink-0 items-center justify-center rounded bg-brand-50 text-brand-700"
        style={{ width: size, height: size }}
      >
        <Building2 size={Math.round(size * 0.62)} />
      </span>
    );

  if (isCollapsed) {
    return (
      <button
        type="button"
        aria-label={`Current workspace: ${name}. Switch workspace`}
        title={name}
        onClick={() => openSwitcher(true)}
        className="my-2 mx-auto flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 transition-colors cursor-pointer"
      >
        {renderLogo(22)}
      </button>
    );
  }

  return (
    <div className="px-1 pt-4 pb-1">
      <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
        Current Workspace
      </p>
      <button
        type="button"
        onClick={() => openSwitcher(true)}
        aria-label={`Current workspace: ${name}. Switch workspace`}
        title={`${name} — switch workspace`}
        className="w-full flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-2.5 py-2 text-sm hover:bg-zinc-100 transition-colors cursor-pointer"
      >
        {renderLogo(24)}
        <span className="flex-1 min-w-0 truncate text-left font-medium text-zinc-800">{name}</span>
        <ChevronDown size={15} className="shrink-0 text-zinc-400" />
      </button>
    </div>
  );
};

// Internal Link Component (unchanged)
const NavItem = ({ item, currentPath, onLinkClick, isCollapsed }: { item: NavItemType; currentPath: string; onLinkClick: () => void; isCollapsed: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const role = useUserStore((s) => s.role);
  const user = useUserStore((s) => s.user);
  const effectiveUserRoles = role ? [role as UserRole] : (user?.roles || []);
  
  // Filter children based on user role
  const filteredChildren = item.items?.filter((child) => {
    if (!child.roles || child.roles.length === 0) return true;
    return child.roles.some((r) => effectiveUserRoles.includes(r));
  });
  
  const hasChildren = filteredChildren && filteredChildren.length > 0;
  
  // Enhanced active check: exact match OR if current path matches any of the activeOnPaths
  const isActive = currentPath === item.href || 
    (item.activeOnPaths && item.activeOnPaths.some(path => currentPath === path));

  // Redesign: solid brand pill for the active item, matching the vendor's
  // sidebar. Collapsed items become a square 44px tap target.
  const baseLinkStyle = "flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors select-none";
  const collapsedLinkStyle = "h-11 w-11 justify-center px-0";
  const activeStyle = "bg-brand-700 text-white";
  const inactiveStyle = "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900";

  if (hasChildren) {
    // When collapsed, render children as a right-anchored flyout so
    // they remain reachable without expanding the sidebar.
    if (isCollapsed) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={item.title}
              className={cn(baseLinkStyle, collapsedLinkStyle, isActive ? "bg-brand-50 text-brand-700" : inactiveStyle)}
            >
              <IconRenderer icon={item.icon} size={20} iconType={item.iconType} isActive={isActive} className={isActive ? "text-brand-700" : "text-zinc-500"} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56 ml-2">
            <div className="px-2 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              {item.title}
            </div>
            {filteredChildren!.map((sub) => {
              const subActive = currentPath === sub.href;
              return (
                <DropdownMenuItem key={sub.href} asChild>
                  <NavLink
                    to={sub.href}
                    onClick={onLinkClick}
                    className={cn(
                      "block w-full px-3 py-2 text-sm rounded-md cursor-pointer",
                      subActive ? "text-brand-700 font-medium bg-brand-50" : "text-zinc-700 hover:text-zinc-900"
                    )}
                  >
                    {sub.title}
                  </NavLink>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(baseLinkStyle, "w-full justify-between", isActive && !isOpen ? "bg-brand-50 text-brand-700" : inactiveStyle)}
        >
          <div className="flex items-center gap-3">
            <IconRenderer icon={item.icon} size={20} iconType={item.iconType} isActive={isActive && !isOpen} className={isActive && !isOpen ? "text-brand-700" : "text-zinc-500"} />
            <span>{item.title}</span>
          </div>
          <ChevronDown size={16} className={cn("transition-transform duration-200", isOpen && "rotate-180")} />
        </button>

        {isOpen && filteredChildren && (
          <div className="ml-9 mt-1 space-y-1 border-l-2 border-zinc-100 pl-2">
            {filteredChildren.map((sub) => (
              <NavLink
                key={sub.href}
                to={sub.href}
                onClick={onLinkClick}
                className={({ isActive }) =>
                  cn("block px-3 py-2 text-sm rounded-md transition-colors", isActive ? "text-brand-700 font-medium bg-brand-50" : "text-zinc-500 hover:text-zinc-900")
                }
              >
                {sub.title}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      end
      onClick={onLinkClick}
      title={isCollapsed ? item.title : undefined}
      className={cn(baseLinkStyle, isActive ? activeStyle : inactiveStyle, isCollapsed && collapsedLinkStyle)}
    >
      <>
        <IconRenderer icon={item.icon} size={20} iconType={item.iconType} isActive={isActive} className={cn("shrink-0", isActive ? "text-white" : "text-zinc-500")} />
        {!isCollapsed && <span>{item.title}</span>}
      </>
    </NavLink>
  );
};